import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  Box, 
  Typography, 
  Tabs, 
  Tab, 
  useTheme, 
  useMediaQuery,
  CircularProgress
} from '@mui/material';
import { 
  School as SchoolIcon,
  Payment as PaymentIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

// Import tab components
import MyCoursesTab from './Components/MyCoursesTab';
import MyPaymentsTab from './Components/MyPaymentsTab';
import RenewCourseTab from './Components/RenewCourseTab';

const MyCourses = React.memo(() => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1020px)');
  const location = useLocation();
  
  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);

  // Tab configuration
  const tabs = useMemo(() => [
    {
      label: 'My Courses',
      icon: <SchoolIcon />,
      component: MyCoursesTab
    },
    {
      label: 'My Payments',
      icon: <PaymentIcon />,
      component: MyPaymentsTab
    },
    {
      label: 'Renew Course',
      icon: <RefreshIcon />,
      component: RenewCourseTab
    }
  ], []);

  // Handle tab change
  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  // Get current tab component
  const CurrentTabComponent = tabs[activeTab]?.component;

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: 2, 
      maxWidth: '100%'
    }}>
      {/* Page Title */}
      <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 600 }}>
        My Courses
      </Typography>

      {/* Horizontal Tabs */}
      <Box sx={{ 
        borderBottom: 1, 
        borderColor: 'divider',
        backgroundColor: 'white',
        borderRadius: 1,
        px: 2
      }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.primary.main,
              height: 3
            },
            '& .MuiTab-root': {
              minHeight: '48px',
              textTransform: 'none',
              fontWeight: 600,
              '&.Mui-selected': {
                color: theme.palette.primary.main
              }
            }
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Box>

      {/* Content Area */}
      <Box sx={{ 
        backgroundColor: 'white',
        borderRadius: 1,
        minHeight: '400px'
      }}>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            height: '400px',
            gap: 2
          }}>
            <CircularProgress size={40} />
            <Typography variant="body1" color="text.secondary">
              Loading course information...
            </Typography>
          </Box>
        ) : CurrentTabComponent ? (
          <CurrentTabComponent />
        ) : (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            height: '400px',
            gap: 2,
            p: 3
          }}>
            <SchoolIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
            <Typography variant="h6" color="text.secondary">
              No Courses Available
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Start by creating your first course.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
});

MyCourses.displayName = 'MyCourses';

export default MyCourses;
